import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const PREVIEW_CHARS = 220;

/**
 * Renders a personal monthly welcome note from the host on the Dashboard.
 * If a sign-up welcome exists and the current user has not yet seen it, also
 * shows a one-time modal on first dashboard visit.
 */
export default function WelcomeNote({ user }) {
  const [monthly, setMonthly] = useState(null);
  const [signupMsg, setSignupMsg] = useState(null);
  const [signupSeen, setSignupSeen] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showSignup, setShowSignup] = useState(false);

  useEffect(() => {
    fetch('/api/welcome-messages', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        setMonthly(d.messages?.monthly || null);
        setSignupMsg(d.messages?.signup || null);
        setSignupSeen(!!d.signup_seen);
        if (d.messages?.signup && !d.signup_seen) setShowSignup(true);
      })
      .catch(() => {});
  }, []);

  const dismissSignup = async () => {
    try {
      await fetch('/api/welcome-messages/signup/seen', { method: 'POST', credentials: 'include' });
      setSignupSeen(true);
      setShowSignup(false);
    } catch {
      toast.error('Could not mark welcome as seen');
    }
  };

  if (!monthly && !signupMsg) return null;

  const content = monthly?.content || '';
  const isLong = content.length > PREVIEW_CHARS;
  const visible = expanded || !isLong ? content : content.slice(0, PREVIEW_CHARS).trimEnd() + '…';

  return (
    <>
      {monthly && (
        <Card className="p-4 md:p-5 mb-4 md:mb-6 bg-gradient-to-br from-amber-50/40 via-white to-emerald-50/30 border-brand-sage/20" data-testid="welcome-note">
          <div className="flex items-start gap-3 md:gap-4">
            <img
              src={monthly.author_picture || `/api/members/${monthly.author_id}/avatar`}
              alt={monthly.author_name}
              className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover border-2 border-white shadow-sm flex-shrink-0"
              data-testid="welcome-author-avatar"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-xs font-bold uppercase tracking-wide text-brand-sage flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> A note from {monthly.author_name?.split(' ')[0] || 'the team'}
                </span>
                <span className="text-[10px] text-slate-400">
                  {monthly.updated_at ? new Date(monthly.updated_at).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : ''}
                </span>
              </div>
              {monthly.title && (
                <h3 className="text-base md:text-lg font-bold text-slate-800 mt-0.5 mb-1.5" data-testid="welcome-title">{monthly.title}</h3>
              )}
              <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line" data-testid="welcome-body">{visible}</p>
              {isLong && (
                <Button variant="ghost" size="sm" onClick={() => setExpanded(v => !v)}
                  className="mt-2 text-brand-navy hover:text-brand-dark px-0 h-7" data-testid="welcome-toggle">
                  {expanded ? <>Show less <ChevronUp className="h-3.5 w-3.5 ml-1" /></> : <>Read more <ChevronDown className="h-3.5 w-3.5 ml-1" /></>}
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      <Dialog open={showSignup} onOpenChange={(v) => { if (!v) dismissSignup(); }}>
        <DialogContent className="max-w-lg" data-testid="signup-welcome-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {signupMsg?.author_picture && (
                <img src={signupMsg.author_picture} alt={signupMsg.author_name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm" />
              )}
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-brand-sage">A note from {signupMsg?.author_name?.split(' ')[0] || 'the team'}</p>
                <p className="text-lg font-bold text-slate-800">{signupMsg?.title || 'Welcome!'}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{signupMsg?.content}</p>
          <DialogFooter>
            <Button onClick={dismissSignup} className="bg-gradient-to-r from-brand-navy to-brand-sage text-white" data-testid="signup-welcome-dismiss">
              Got it, let's go
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
